import { useCallback, useEffect, useState } from "react";
import { listarEntregasPorEstudiante, upsertEntregablesMatricula } from "../../../../services/entregasService";
import { generarDiplomaPorMatricula, descargarDiplomaPorMatricula } from "../../../../services/diplomasService";
import {
  generarMencionHonorPorMatricula,
  descargarMencionHonorPorMatricula,
} from "../../../../services/mencionesHonorService";
import { showToast } from "../../../../utils/showToast";

// Los unicos entregables tipo='documento' que hoy tienen un generador real
// detras (Diploma, Mencion de honor). El resto (ej. Credencial institucional /
// futuro carnet) todavia no tiene generador propio — se administran como un
// entregable normal (solo cambio de estado) hasta que se construya esa fase.
const tipoGenerador = (nombre = "") => {
  const n = nombre.trim().toLowerCase();
  if (n === "diploma") return "diploma";
  if (n.startsWith("mención") || n.startsWith("mencion")) return "mencion";
  return null;
};

/**
 * Entregables de TODAS las matriculas de un estudiante, agrupados por
 * matricula. A diferencia de una vista de solo consulta, aqui si se puede
 * administrar: cambiar estado/talla/observacion (reutiliza el mismo
 * endpoint batch que usa el modal de Matricula, enviando solo el item que
 * cambio) y generar/descargar Diploma y Mencion de honor.
 */
export default function useEntregablesEstudiante(estudianteId) {
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);

  const cargar = useCallback(async () => {
    if (!estudianteId) {
      setGrupos([]);
      return;
    }
    setLoading(true);
    try {
      const data = await listarEntregasPorEstudiante(estudianteId);
      setGrupos(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      console.error(err);
      const message = err?.message || "No se pudieron cargar los entregables.";
      setError(message);
      showToast({ type: "danger", message });
    } finally {
      setLoading(false);
    }
  }, [estudianteId]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const guardarEntregable = useCallback(
    async (item, cambios = {}) => {
      setSavingId(item.id);
      try {
        await upsertEntregablesMatricula(item.matricula_id, [
          {
            entregable_id: item.entregable_id,
            estado: cambios.estado ?? item.estado,
            talla: cambios.talla ?? item.talla ?? null,
            observaciones: cambios.observaciones ?? item.observacion ?? null,
          },
        ]);
        await cargar();
        showToast({ type: "success", message: "Entregable actualizado." });
        return true;
      } catch (err) {
        console.error(err);
        showToast({ type: "danger", message: err?.message || "No se pudo actualizar el entregable." });
        return false;
      } finally {
        setSavingId(null);
      }
    },
    [cargar]
  );

  const generarDocumento = useCallback(
    async (item) => {
      const tipo = tipoGenerador(item.nombre);
      if (!tipo) return false;
      setSavingId(item.id);
      try {
        if (tipo === "diploma") await generarDiplomaPorMatricula(item.matricula_id);
        else await generarMencionHonorPorMatricula(item.matricula_id);
        await cargar();
        showToast({ type: "success", message: `${item.nombre} generado correctamente.` });
        return true;
      } catch (err) {
        console.error(err);
        showToast({ type: "danger", message: err?.message || `No se pudo generar ${item.nombre}.` });
        return false;
      } finally {
        setSavingId(null);
      }
    },
    [cargar]
  );

  const descargarDocumento = useCallback(async (item) => {
    try {
      if (item.descarga_tipo === "diploma") await descargarDiplomaPorMatricula(item.matricula_id);
      else if (item.descarga_tipo === "mencion") await descargarMencionHonorPorMatricula(item.matricula_id);
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: err?.message || "No se pudo descargar el documento." });
    }
  }, []);

  return {
    grupos,
    loading,
    error,
    savingId,
    recargar: cargar,
    guardarEntregable,
    generarDocumento,
    descargarDocumento,
    tieneGenerador: (nombre) => Boolean(tipoGenerador(nombre)),
  };
}
